import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { LogOut, Radio, RefreshCw, Trash2, Loader2, CreditCard, Banknote, Wallet, FileText } from "lucide-react";
import { format } from "date-fns";
import { fr } from "date-fns/locale";
import { toast } from "sonner";

import { adminApi } from "@/lib/api";
import SEO from "@/components/SEO";
import BookingCalendar from "@/components/BookingCalendar";
import { Button } from "@/components/ui/button";
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "@/components/ui/table";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
} from "@/components/ui/dialog";

const PAYMENT_STATUSES = [
    { value: "pending", label: "En attente" },
    { value: "paid", label: "Payé" },
    { value: "failed", label: "Échec" },
    { value: "expired", label: "Expiré" },
];
const BOOKING_STATUSES = [
    { value: "new", label: "Nouveau" },
    { value: "confirmed", label: "Confirmé" },
    { value: "cancelled", label: "Annulé" },
    { value: "completed", label: "Terminé" },
];
const PAYMENT_METHODS = [
    { value: "", label: "—" },
    { value: "stripe_online", label: "Stripe carte" },
    { value: "stripe_klarna", label: "Stripe · Klarna" },
    { value: "scalapay", label: "Scalapay 3×" },
    { value: "card_onsite", label: "Carte sur place" },
    { value: "cash", label: "Espèces" },
    { value: "transfer", label: "Virement" },
    { value: "other", label: "Autre" },
];
const METHOD_LABELS = {
    stripe_online: "Stripe carte",
    stripe_klarna: "Stripe · Klarna",
    scalapay: "Scalapay 3×",
    card_onsite: "Carte sur place",
    cash: "Espèces",
    transfer: "Virement",
    other: "Autre",
};

const PAY_VARIANT = {
    paid: "bg-amber-400/20 text-amber-300 border-amber-400/40",
    pending: "bg-zinc-700/40 text-zinc-300 border-zinc-600/40",
    failed: "bg-red-500/10 text-red-300 border-red-500/40",
    expired: "bg-zinc-800 text-zinc-500 border-zinc-700",
};
const BOOK_VARIANT = {
    new: "bg-blue-500/10 text-blue-300 border-blue-500/30",
    confirmed: "bg-green-500/10 text-green-300 border-green-500/30",
    cancelled: "bg-red-500/10 text-red-300 border-red-500/30",
    completed: "bg-amber-400/10 text-amber-300 border-amber-400/30",
};

export default function AdminDashboard() {
    const navigate = useNavigate();
    const [bookings, setBookings] = useState([]);
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(true);
    const [detail, setDetail] = useState(null);
    const [filter, setFilter] = useState("all");
    const [methodFilter, setMethodFilter] = useState("all");

    useEffect(() => {
        if (!localStorage.getItem("ms_admin_token")) {
            navigate("/admin/login");
            return;
        }
        load();
    }, [navigate]);

    const load = async () => {
        setLoading(true);
        try {
            const [b, s] = await Promise.all([
                adminApi.get("/admin/bookings"),
                adminApi.get("/admin/stats"),
            ]);
            setBookings(b.data.bookings || []);
            setStats(s.data);
        } catch (e) {
            if (e?.response?.status === 401) {
                localStorage.removeItem("ms_admin_token");
                navigate("/admin/login");
            } else {
                toast.error("Erreur de chargement");
            }
        } finally {
            setLoading(false);
        }
    };

    const logout = () => {
        localStorage.removeItem("ms_admin_token");
        navigate("/admin/login");
    };

    const updateField = async (id, patch) => {
        try {
            const { data } = await adminApi.patch(`/admin/bookings/${id}`, patch);
            setBookings((list) => list.map((b) => (b.id === id ? data : b)));
            toast.success("Mis à jour");
            load();
        } catch (e) {
            toast.error("Erreur");
        }
    };

    const handlePaymentStatusChange = async (b, newStatus) => {
        // If marking as paid and no method yet, default to card_onsite
        const patch = { payment_status: newStatus };
        if (newStatus === "paid" && !b.payment_method) {
            patch.payment_method = "card_onsite";
        }
        if (newStatus !== "paid") {
            patch.payment_method = "";
        }
        await updateField(b.id, patch);
    };

    const remove = async (id) => {
        if (!confirm("Supprimer cette réservation ?")) return;
        try {
            await adminApi.delete(`/admin/bookings/${id}`);
            setBookings((list) => list.filter((b) => b.id !== id));
            toast.success("Supprimée");
            load();
        } catch (e) {
            toast.error("Erreur");
        }
    };

    const exportCsv = () => {
        const rows = filteredBookings.map((b) => ({
            id: b.id,
            date_creation: b.created_at,
            client: `${b.first_name} ${b.last_name}`,
            email: b.email,
            tel: b.phone,
            entreprise: b.company || "",
            formule: b.package_name,
            session_date: b.date,
            session_creneau: b.time_slot,
            total: b.total_amount,
            statut_paiement: b.payment_status,
            methode: b.payment_method ? METHOD_LABELS[b.payment_method] : "",
            paye_le: b.paid_at || "",
            statut_reservation: b.booking_status,
        }));
        const headers = Object.keys(rows[0] || {}).join(",");
        const csv = [headers, ...rows.map((r) => Object.values(r).map((v) => `"${String(v).replaceAll('"', '""')}"`).join(","))].join("\n");
        const blob = new Blob(["\ufeff" + csv], { type: "text/csv;charset=utf-8;" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `mon-seraphin-reservations-${format(new Date(), "yyyy-MM-dd")}.csv`;
        a.click();
        URL.revokeObjectURL(url);
        toast.success(`${rows.length} réservation(s) exportée(s)`);
    };

    const filteredBookings = bookings.filter((b) => {
        if (filter === "paid" && b.payment_status !== "paid") return false;
        if (filter === "pending" && b.payment_status !== "pending") return false;
        if (filter === "new" && b.booking_status !== "new") return false;
        if (filter === "confirmed" && b.booking_status !== "confirmed") return false;
        if (methodFilter !== "all" && (b.payment_method || "") !== methodFilter) return false;
        return true;
    });

    const stripeRev = (stats?.revenue_by_method?.stripe_online?.total || 0) + (stats?.revenue_by_method?.stripe_klarna?.total || 0);
    const klarnaRev = stats?.revenue_by_method?.stripe_klarna?.total || 0;
    const scalapayRev = stats?.revenue_by_method?.scalapay?.total || 0;
    const onsiteRev = stats?.revenue_by_method?.card_onsite?.total || 0;
    const cashRev = stats?.revenue_by_method?.cash?.total || 0;
    const transferRev = stats?.revenue_by_method?.transfer?.total || 0;
    const otherRev = (stats?.revenue_by_method?.other?.total || 0) + (stats?.revenue_by_method?.[""]?.total || 0);

    return (
        <div className="min-h-screen bg-[#050505]" data-testid="admin-dashboard-page">
            <SEO title="Dashboard admin — Mon Séraphin" description="Tableau de bord administrateur." path="/admin" noindex />
            {/* Top bar */}
            <header className="border-b border-white/10 bg-black/60 backdrop-blur-xl sticky top-0 z-30">
                <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <Radio className="w-6 h-6 text-amber-400" strokeWidth={1.5} />
                        <div>
                            <p className="font-display text-white text-lg">Mon Séraphin · Admin</p>
                            <p className="text-xs text-zinc-500 uppercase tracking-widest">Réservations & comptabilité</p>
                        </div>
                    </div>
                    <div className="flex items-center gap-2">
                        <Button onClick={() => navigate("/admin/blog")} variant="ghost" className="text-zinc-400 hover:text-white" data-testid="admin-blog-nav-btn">
                            <Radio className="w-4 h-4 mr-1" /> Blog
                        </Button>
                        <Button onClick={() => navigate("/admin/abonnes")} variant="ghost" className="text-zinc-400 hover:text-white" data-testid="admin-subs-nav-btn">
                            <Wallet className="w-4 h-4 mr-1" /> Abonnés
                        </Button>
                        <Button onClick={exportCsv} variant="ghost" className="text-zinc-400 hover:text-white" data-testid="admin-export-btn">
                            <FileText className="w-4 h-4 mr-1" /> Exporter CSV
                        </Button>
                        <Button onClick={load} variant="ghost" className="text-zinc-400 hover:text-white" data-testid="admin-refresh-btn">
                            <RefreshCw className="w-4 h-4" />
                        </Button>
                        <Button onClick={logout} variant="ghost" className="text-zinc-400 hover:text-white" data-testid="admin-logout-btn">
                            <LogOut className="w-4 h-4 mr-1" /> Déconnexion
                        </Button>
                    </div>
                </div>
            </header>

            <main className="max-w-7xl mx-auto px-6 py-10">
                {/* Stats overview */}
                <div className="grid grid-cols-2 md:grid-cols-5 gap-4" data-testid="admin-stats">
                    {stats && (
                        <>
                            <StatCard label="Réservations" value={stats.total_bookings} sub={`${stats.new_bookings} nouvelles`} />
                            <StatCard label="Confirmées" value={stats.confirmed_bookings} sub={`${stats.paid_bookings} payées`} />
                            <StatCard label="CA encaissé" value={`${stats.revenue_paid.toFixed(0)} €`} accent />
                            <StatCard label="En attente" value={`${stats.outstanding_total.toFixed(0)} €`} sub={`${stats.outstanding_count} commande(s)`} highlight="orange" />
                            <StatCard label="Taux conversion" value={stats.total_bookings ? `${Math.round((stats.paid_bookings / stats.total_bookings) * 100)}%` : "—"} />
                        </>
                    )}
                </div>

                {/* Comptabilité par méthode */}
                {stats && (
                    <div className="mt-6 rounded-2xl border border-white/10 bg-[#0a0a0a] p-6">
                        <div className="flex items-center justify-between mb-5">
                            <h2 className="font-display text-lg text-white font-medium">Comptabilité par mode de paiement</h2>
                            <span className="text-xs text-zinc-500">Total encaissé: <span className="text-amber-300 font-display text-base">{stats.revenue_paid.toFixed(2)} €</span></span>
                        </div>
                        <div className="grid grid-cols-2 md:grid-cols-7 gap-4">
                            <MethodCard icon={CreditCard} label="Stripe carte" amount={stats.revenue_by_method?.stripe_online?.total || 0} count={stats.revenue_by_method?.stripe_online?.count || 0} />
                            <MethodCard icon={CreditCard} label="Klarna (3-4×)" amount={klarnaRev} count={stats.revenue_by_method?.stripe_klarna?.count || 0} />
                            <MethodCard icon={CreditCard} label="Scalapay 3×" amount={scalapayRev} count={stats.revenue_by_method?.scalapay?.count || 0} />
                            <MethodCard icon={CreditCard} label="Carte sur place" amount={onsiteRev} count={stats.revenue_by_method?.card_onsite?.count || 0} />
                            <MethodCard icon={Banknote} label="Espèces" amount={cashRev} count={stats.revenue_by_method?.cash?.count || 0} />
                            <MethodCard icon={Wallet} label="Virement" amount={transferRev} count={stats.revenue_by_method?.transfer?.count || 0} />
                            <MethodCard icon={Wallet} label="Autre" amount={otherRev} count={(stats.revenue_by_method?.other?.count || 0) + (stats.revenue_by_method?.[""]?.count || 0)} />
                        </div>
                    </div>
                )}

                {/* Calendrier studio — semaine, avec noms clients */}
                <div className="mt-6 rounded-2xl border border-white/10 bg-[#0a0a0a] p-6" data-testid="admin-calendar-section">
                    <h2 className="font-display text-lg text-white font-medium mb-5">Calendrier du studio</h2>
                    <BookingCalendar duration={60} value={null} onChange={() => {}} adminMode readOnly />
                </div>

                {/* Filters */}
                <div className="mt-8 flex flex-wrap items-center gap-2">
                    {[
                        ["all", "Toutes"],
                        ["new", "Nouvelles"],
                        ["confirmed", "Confirmées"],
                        ["paid", "Payées"],
                        ["pending", "En attente"],
                    ].map(([k, l]) => (
                        <button
                            key={k}
                            onClick={() => setFilter(k)}
                            className={`rounded-full px-4 py-1.5 text-xs font-body border transition ${
                                filter === k
                                    ? "bg-amber-400 text-black border-amber-400"
                                    : "border-white/10 text-zinc-400 hover:border-amber-400/40 hover:text-amber-300"
                            }`}
                            data-testid={`admin-filter-${k}`}
                        >
                            {l}
                        </button>
                    ))}
                    <div className="ml-auto">
                        <Select value={methodFilter} onValueChange={setMethodFilter}>
                            <SelectTrigger className="w-[200px] border-white/10 bg-white/[0.02] text-zinc-300" data-testid="admin-method-filter">
                                <SelectValue placeholder="Mode de paiement" />
                            </SelectTrigger>
                            <SelectContent className="bg-[#0a0a0a] border-white/10 text-white">
                                <SelectItem value="all">Tous les modes</SelectItem>
                                {PAYMENT_METHODS.filter((m) => m.value !== "").map((m) => (
                                    <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                </div>

                {/* Table */}
                <div className="mt-4 rounded-2xl border border-white/10 bg-[#0a0a0a] overflow-hidden">
                    <div className="p-5 flex items-center justify-between border-b border-white/5">
                        <h2 className="font-display text-xl text-white font-medium">Réservations</h2>
                        <span className="text-xs text-zinc-500">{filteredBookings.length} sur {bookings.length}</span>
                    </div>
                    {loading ? (
                        <div className="p-16 flex justify-center">
                            <Loader2 className="w-6 h-6 animate-spin text-amber-400" />
                        </div>
                    ) : filteredBookings.length === 0 ? (
                        <div className="p-16 text-center text-zinc-500">Aucune réservation.</div>
                    ) : (
                        <div className="overflow-x-auto">
                            <Table>
                                <TableHeader>
                                    <TableRow className="border-white/10 hover:bg-transparent">
                                        <TableHead className="text-zinc-400 uppercase text-xs tracking-widest">Créé</TableHead>
                                        <TableHead className="text-zinc-400 uppercase text-xs tracking-widest">Client</TableHead>
                                        <TableHead className="text-zinc-400 uppercase text-xs tracking-widest">Formule</TableHead>
                                        <TableHead className="text-zinc-400 uppercase text-xs tracking-widest">Session</TableHead>
                                        <TableHead className="text-zinc-400 uppercase text-xs tracking-widest">Total</TableHead>
                                        <TableHead className="text-zinc-400 uppercase text-xs tracking-widest">Paiement</TableHead>
                                        <TableHead className="text-zinc-400 uppercase text-xs tracking-widest">Méthode</TableHead>
                                        <TableHead className="text-zinc-400 uppercase text-xs tracking-widest">Statut</TableHead>
                                        <TableHead className="text-zinc-400 uppercase text-xs tracking-widest">Actions</TableHead>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {filteredBookings.map((b) => (
                                        <TableRow key={b.id} className="border-white/5 hover:bg-white/[0.02]">
                                            <TableCell className="text-zinc-400 text-xs">
                                                {format(new Date(b.created_at), "d MMM HH:mm", { locale: fr })}
                                            </TableCell>
                                            <TableCell>
                                                <button onClick={() => setDetail(b)} className="text-left hover:text-amber-300 transition" data-testid={`admin-view-${b.id}`}>
                                                    <p className="text-white font-medium">{b.first_name} {b.last_name}</p>
                                                    <p className="text-xs text-zinc-500">{b.email}</p>
                                                </button>
                                            </TableCell>
                                            <TableCell className="text-white">{b.package_name}</TableCell>
                                            <TableCell className="text-zinc-400 text-xs">
                                                {b.time_slot
                                                    ? `${b.date} · ${b.time_slot} (${(b.duration_minutes || 60) / 60}h)`
                                                    : `${b.date} · activation`}
                                            </TableCell>
                                            <TableCell className="text-amber-300 font-display">{b.total_amount}€</TableCell>
                                            <TableCell>
                                                <Select value={b.payment_status} onValueChange={(v) => handlePaymentStatusChange(b, v)}>
                                                    <SelectTrigger className={`w-[120px] border ${PAY_VARIANT[b.payment_status]}`} data-testid={`admin-payment-select-${b.id}`}>
                                                        <SelectValue />
                                                    </SelectTrigger>
                                                    <SelectContent className="bg-[#0a0a0a] border-white/10 text-white">
                                                        {PAYMENT_STATUSES.map((s) => (
                                                            <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                                                        ))}
                                                    </SelectContent>
                                                </Select>
                                            </TableCell>
                                            <TableCell>
                                                {b.payment_status === "paid" ? (
                                                    <Select
                                                        value={b.payment_method || ""}
                                                        onValueChange={(v) => updateField(b.id, { payment_method: v })}
                                                    >
                                                        <SelectTrigger className="w-[160px] border-white/10 bg-white/[0.02] text-zinc-200 text-xs" data-testid={`admin-method-select-${b.id}`}>
                                                            <SelectValue placeholder="—" />
                                                        </SelectTrigger>
                                                        <SelectContent className="bg-[#0a0a0a] border-white/10 text-white">
                                                            {PAYMENT_METHODS.filter((m) => m.value !== "").map((m) => (
                                                                <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>
                                                            ))}
                                                        </SelectContent>
                                                    </Select>
                                                ) : b.payment_choice === "scalapay" ? (
                                                    <span className="text-xs text-amber-300/80" data-testid={`admin-method-initiated-${b.id}`}>Scalapay (initié)</span>
                                                ) : b.payment_choice === "stripe_now" ? (
                                                    <span className="text-xs text-amber-300/80" data-testid={`admin-method-initiated-${b.id}`}>Stripe (initié)</span>
                                                ) : (
                                                    <span className="text-xs text-zinc-600">—</span>
                                                )}
                                            </TableCell>
                                            <TableCell>
                                                <Select value={b.booking_status} onValueChange={(v) => updateField(b.id, { booking_status: v })}>
                                                    <SelectTrigger className={`w-[130px] border ${BOOK_VARIANT[b.booking_status]}`} data-testid={`admin-status-select-${b.id}`}>
                                                        <SelectValue />
                                                    </SelectTrigger>
                                                    <SelectContent className="bg-[#0a0a0a] border-white/10 text-white">
                                                        {BOOKING_STATUSES.map((s) => (
                                                            <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                                                        ))}
                                                    </SelectContent>
                                                </Select>
                                            </TableCell>
                                            <TableCell>
                                                <Button variant="ghost" size="icon" onClick={() => remove(b.id)} className="text-zinc-500 hover:text-red-400" data-testid={`admin-delete-${b.id}`}>
                                                    <Trash2 className="w-4 h-4" />
                                                </Button>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </div>
                    )}
                </div>
            </main>

            {/* Detail dialog */}
            <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
                <DialogContent className="bg-[#0a0a0a] border border-white/10 text-white max-w-lg">
                    <DialogHeader>
                        <DialogTitle className="font-display text-2xl font-light">Détail réservation</DialogTitle>
                    </DialogHeader>
                    {detail && (
                        <div className="space-y-4 text-sm">
                            <Row label="Client" value={`${detail.first_name} ${detail.last_name}`} />
                            <Row label="Email" value={detail.email} />
                            <Row label="Téléphone" value={detail.phone} />
                            <Row label="Entreprise" value={detail.company || "—"} />
                            <Row label="Formule" value={`${detail.package_name} (${detail.package_price}€)`} />
                            <Row label="Date session" value={`${detail.date} · ${detail.time_slot}`} />
                            {detail.upsells?.length > 0 && (
                                <div>
                                    <p className="text-zinc-500 uppercase text-xs tracking-widest mb-2">Options</p>
                                    <ul className="space-y-1">
                                        {detail.upsells.map((u, i) => (
                                            <li key={i} className="flex justify-between">
                                                <span>{u.name} × {u.quantity}</span>
                                                <span className="text-amber-300">{u.line_total}€</span>
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            )}
                            <Row label="Total" value={`${detail.total_amount}€`} emphasis />
                            <Row label="Choix initial" value={detail.payment_choice === "stripe_now" ? "Paiement en ligne" : "Plus tard"} />
                            {detail.payment_method && (
                                <Row label="Méthode encaissement" value={METHOD_LABELS[detail.payment_method] || detail.payment_method} />
                            )}
                            {detail.paid_at && (
                                <Row label="Payé le" value={format(new Date(detail.paid_at), "d MMM yyyy HH:mm", { locale: fr })} />
                            )}
                            {detail.project_description && (
                                <div>
                                    <p className="text-zinc-500 uppercase text-xs tracking-widest mb-1">Description projet</p>
                                    <p className="text-zinc-300 whitespace-pre-wrap">{detail.project_description}</p>
                                </div>
                            )}
                        </div>
                    )}
                </DialogContent>
            </Dialog>
        </div>
    );
}

function StatCard({ label, value, sub, accent, highlight }) {
    const cls = accent
        ? "border-amber-400/30 bg-amber-400/[0.05]"
        : highlight === "orange"
        ? "border-orange-500/30 bg-orange-500/[0.05]"
        : "border-white/10 bg-white/[0.02]";
    return (
        <div className={`rounded-2xl border p-5 ${cls}`}>
            <p className="text-xs text-zinc-500 uppercase tracking-widest">{label}</p>
            <p className={`mt-2 font-display text-3xl font-light ${accent ? "text-amber-400" : highlight === "orange" ? "text-orange-300" : "text-white"}`}>{value}</p>
            {sub && <p className="mt-1 text-xs text-zinc-500">{sub}</p>}
        </div>
    );
}

function MethodCard({ icon: Icon, label, amount, count }) {
    return (
        <div className="rounded-xl border border-white/5 bg-white/[0.02] p-4">
            <div className="flex items-center gap-2 text-zinc-500">
                <Icon className="w-4 h-4 text-amber-400" />
                <span className="text-[11px] uppercase tracking-widest">{label}</span>
            </div>
            <p className="mt-2 font-display text-2xl text-white font-light">{amount.toFixed(0)} €</p>
            <p className="text-xs text-zinc-500">{count} commande{count > 1 ? "s" : ""}</p>
        </div>
    );
}

function Row({ label, value, emphasis }) {
    return (
        <div className="flex justify-between items-baseline gap-4">
            <span className="text-zinc-500 uppercase text-xs tracking-widest">{label}</span>
            <span className={emphasis ? "font-display text-xl text-amber-400" : "text-white"}>{value}</span>
        </div>
    );
}
